import { useState } from "react";
import { ApiError, post } from "../api";
import { wordsOf } from "../api";
import { label } from "../components/Money";
import { Loading, Refused } from "../components/Shell";
import { useApi } from "../useApi";
import { Scrolls } from "../components/Table";

export function Approvals() {
  const { data, error, loading, reload } = useApi<any>("/approvals");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;

  async function decide(id: string, decision: string) {
    setBusy(true); setProblem(null);
    try { await post(`/approvals/${id}/decide`, { decision }); reload(); }
    catch (e) { setProblem(e instanceof ApiError ? e.detail : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <h1>Write-back approvals</h1>
      <p className="standfirst">
        Writing to the contract register has contractual force — a notification
        created there is a notification given. Nothing is written without a
        person authorising this exact text.
      </p>

      {!data.enabled ? (
        <div className="note amber">
          <span className="h">Write-back is switched off</span>
          Nothing can be written to the register while it is off, which is the
          shipped default. Drafts are still produced and still need sending by hand.
        </div>
      ) : (
        <>
          <div className="kpis">
            <div><div className="v">{data.mode}</div>
              <div className="k">{data.mode === "dry_run" ? "records, writes nothing" : "writes to CEMAR"}</div></div>
            <div><div className="v">{data.opted_in.length}</div>
              <div className="k">of {Object.keys(data.write_types).length} types opted in</div></div>
            <div><div className="v">{data.approvals.filter((a: any) => a.state === "pending").length}</div>
              <div className="k">awaiting a decision</div></div>
            <div><div className="v">{data.second_person ? "two" : "one"}</div>
              <div className="k">people required</div></div>
          </div>
          {data.mode === "live" && (
            <div className="note red"><span className="h">Live mode</span>
              An approved dispatch creates a real record in CEMAR. There is no undo.</div>
          )}
        </>
      )}

      {data.approvals.length > 0 ? (
        <Scrolls>
          <table>
            <thead><tr><th>Requested</th><th>Type</th><th>Subject</th><th>Asked by</th><th>State</th><th>Decision</th></tr></thead>
            <tbody>
              {data.approvals.map((a: any) => (
                <tr key={a.id}>
                  <td className="mn">{a.requested_at?.slice(0, 16).replace("T", " ") ?? "—"}</td>
                  {/* A dispatched write of a type the policy now forbids
                      reads as a control failure unless the reader is told
                      the policy moved. It is a real case: four types were
                      exercised in dry run and narrowed back afterwards. */}
                  <td>
                    {data.write_types[a.write_type] ?? a.write_type}
                    {!data.opted_in.includes(a.write_type) && (
                      <span className="k" style={{ display: "block", marginTop: 4 }}
                        title="This write type is no longer opted in. The request was permitted when it was made; the policy has been narrowed since.">
                        no longer enabled
                      </span>
                    )}
                  </td>
                  <td title={wordsOf(a.payload_subject) ?? undefined} style={{ maxWidth: "32ch", overflow: "hidden",
                       textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {wordsOf(a.payload_subject)
                      ?? <span className="withheld">subject withheld — the other side's write</span>}
                  </td>
                  <td>{a.requested_by}</td>
                  {/* Dispatched and rejected are finished; colouring them
                      spent the signal on rows nobody has to touch. Only a
                      request waiting for a decision, or one that failed on
                      the way out, asks anything of the reader. */}
                  <td><span className={`pill ${a.state === "pending" ? "act"
                    : a.state === "failed" ? "alarm"
                    : ["dispatched", "rejected", "stale"].includes(a.state) ? "done" : ""}`}>
                    {label(a.state)}</span></td>
                  <td>
                    {a.state === "pending" && (
                      !data.may_approve ? <span className="withheld">not yours to approve</span>
                      : data.second_person && a.requested_by === data.me
                        ? <span className="withheld">you asked for this</span>
                        : <span style={{ display: "flex", gap: 6 }}>
                            <button className="btn small" disabled={busy}
                              onClick={() => decide(a.id, "approve")}>Approve</button>
                            <button className="btn small" disabled={busy}
                              onClick={() => decide(a.id, "reject")}>Reject</button>
                          </span>
                    )}
                    {a.state === "approved" && (
                      a.may_dispatch ? (
                        <button className="primary small" disabled={busy}
                          onClick={() => decide(a.id, "dispatch")}>
                          {a.target === "dry_run" ? "Dispatch (dry run)" : "Write to CEMAR"}
                        </button>
                      ) : (
                        /* Approved before the policy changed. The server would
                           refuse the dispatch; a button that does what the banner
                           says cannot happen is worse than no button. */
                        <span className="withheld" title="Approved, but write-back is off or this type is no longer opted in. The server checks the policy as it stands now.">
                          approved · write-back off
                        </span>
                      )
                    )}
                    {a.state === "dispatched" && <span className="mn withheld">{a.dispatch_ref}</span>}
                    {a.state === "stale" && <span className="withheld">draft changed — request again</span>}
                    {a.state === "failed" && <span className="withheld">{a.dispatch_error?.slice(0, 60)}</span>}
                    {a.state === "rejected" && <span className="withheld">{a.decided_by}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scrolls>
      ) : (
        <p className="empty">No write-back has been requested. Requests start on the follow-up board.</p>
      )}
      {problem && <div className="err">{problem}</div>}

      <section className="card">
        <h2 className="secttl">What each type would write</h2>
        <Scrolls>
          <table>
            <thead><tr><th>Type</th><th>Act</th><th>Opted in</th></tr></thead>
            <tbody>
              {Object.entries(data.write_types).map(([key, description]) => (
                <tr key={key}>
                  <td className="mn">{key}</td>
                  <td>{String(description)}</td>
                  {/* Opted in is the higher-consequence setting, not the
                      good one: it means this contractual act can be written
                      to the register. Green read as reassurance. */}
                  <td><span className={`pill${data.opted_in.includes(key) ? " on" : " done"}`}>
                    {data.opted_in.includes(key) ? "yes" : "no"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scrolls>
        <p className="hint">
          Types are opted into one at a time: enabling a reply to a quotation
          should not also enable notifying a compensation event.
        </p>
      </section>
    </>
  );
}
